import { cleanObjectKeys, isVideoFile } from '../functions/utils';
import { PagingInfo } from './pagingInfo';
import moment from 'moment';
import { mappingVideoFile } from './videoFile';
import { TYPE_IMAGE, TYPE_VIDEO_FILE } from '../constants/messageTypes';
import { IRecentlySentPhoto } from '../interfaces/IRecentlySentPhoto';

export const MEDIA_TYPE = {
  PHOTO: 'Photo',
  VIDEO: 'Video',
  AUDIO: 'Audio',
} as const;

/**
 * MediaType represents the values of MEDIA_TYPE: 'Photo' | 'Video' | 'Audio'
 */
export type MediaType = (typeof MEDIA_TYPE)[keyof typeof MEDIA_TYPE];

interface File {
  uri: string;
  fileName: string;
  fileType: string;
  data: string;
  fileSize: string;
  width: string;
  height: string;
  browserUpload: boolean;
}

interface Message {
  body: {
    type: string;
    data: any;
  };
  expts?: number;
}

export interface Media {
  mediaType: string;
  data: any;
  expts: number;
  dateCreated?: Date | undefined;
}

export default class RecentlySentPhotoModel {
  approvalStatus: string;
  fullUrl: string;
  isUserImage: boolean;
  photoId: number;
  thumbUrl: string;
  isSelected: boolean;
  expts: number;

  constructor() {
    this.approvalStatus = '';
    this.fullUrl = '';
    this.isUserImage = false;
    this.photoId = -1;
    this.thumbUrl = '';
    this.isSelected = false;
    this.expts = 0;
  }

  static mappingPhoto(object: any): IRecentlySentPhoto {
    const obj = new RecentlySentPhotoModel();
    if (!object) {
      return obj;
    }
    obj.approvalStatus = object.ApprovalStatus || '';
    obj.fullUrl = object.FullUrl || '';
    obj.isUserImage = object.IsUserImage || false;
    obj.photoId = object.PhotoID || -1;
    obj.thumbUrl = object.ThumbUrl || '';
    obj.isSelected = false;

    return obj;
  }

  static mappingMedia(o: any): Media | {} {
    if (o) {
      const obj: Media = {
        mediaType: o.MediaType,
        data: {},
        expts: 0,
        dateCreated: undefined,
      };
      obj.dateCreated = moment(o.DateCreated).toDate();
      switch (o.MediaType) {
        case MEDIA_TYPE.PHOTO:
          obj.data = RecentlySentPhotoModel.mappingPhoto(o.Data);
          break;
        case MEDIA_TYPE.VIDEO:
          obj.data = mappingVideoFile(o.Data);
          break;
        default:
          break;
      }
      return obj;
    }
    return {};
  }

  static mappingMessageMedia(message: Message): Media | null {
    if (!message || !message.body || !message.body.data) {
      return null;
    }
    const isImage = message?.body?.type === TYPE_IMAGE;
    if (isImage) {
      const data = new RecentlySentPhotoModel();
      data.fullUrl = message.body.data;
      return {
        mediaType: MEDIA_TYPE.PHOTO,
        data,
        expts: message?.expts || 0,
      };
    }
    return {
      mediaType: MEDIA_TYPE.VIDEO,
      data: { ...message.body.data }, // body.data is mappingVideoFile
      expts: message?.expts || 0,
    };
  }

  /**
   *
   * @param file: {
        uri: '',
        fileName: '',
        fileType: '',
        data: '',
        fileSize: '',
        width: '',
        height: '',
        browserUpload: true,
      }
   * @return: {
        mediaType: MEDIA_TYPE.VIDEO || MEDIA_TYPE.PHOTO,
        data: { ...message.body.data }, // body.data is mappingVideoFile
        expts: message?.expts || 0,
      }
   */
  static mappingMediaFromFile(file: File): Media {
    const isVideo = isVideoFile(file);
    return {
      expts: 0,
      mediaType: isVideo ? MEDIA_TYPE.VIDEO : MEDIA_TYPE.PHOTO,
      data: isVideo
        ? {
            videoUrl: file.uri,
          }
        : {
            fullUrl: file.uri,
          },
    };
  }

  static mappingListPhoto(array: any[]): IRecentlySentPhoto[] {
    return array.map(itm => this.mappingPhoto(itm));
  }

  static mappingListMedia(a: any[]): Media[] {
    return a.map(itm => this.mappingMedia(itm) as Media);
  }

  static mappingPaginationInfo(object: any): PagingInfo {
    let pagingInfo = new PagingInfo({ Page: object?.PageNumber || 0, TotalItems: object?.RecordCount || 0 });
    pagingInfo = cleanObjectKeys(Object.keys(new PagingInfo()), pagingInfo);
    return pagingInfo;
  }
}
